import { useQueries, useQuery } from "@tanstack/react-query";
import apiClient from "../client";
import type { ChartPayload } from "@scryerlabs/ui/charts";
import { formatMonthYear } from "../../lib/dates";

// Scr.Enums.ChartType. Numeric because that is what the API sends and the store persists.
export enum TradingStrategyChartType {
  Unknown = 0,
  FrontOffice = 1,
  Brochure = 2,
}

// Scr.Enums.StrategyStatus.
export enum TradingStrategyStatus {
  Unknown = 0,
  Draft = 1,
  Published = 2,
  Archived = 3,
}

// Scr.Enums.TradeDirection.
export enum TradingStrategyTradeDirection {
  Unknown = 0,
  Long = 1,
  Short = 2,
}

/**
 * The side a trade was taken on, for display.
 *
 * Not served by a reference endpoint: a direction is never picked or filtered from a list of every
 * possible value, so there is nothing to fetch. Unknown means the archive stated a side we could not
 * read, and renders as a dash rather than being invented.
 */
export const tradeDirectionLabel = (
  direction: TradingStrategyTradeDirection,
): string => {
  switch (direction) {
    case TradingStrategyTradeDirection.Long:
      return "Long";
    case TradingStrategyTradeDirection.Short:
      return "Short";
    default:
      return "-";
  }
};

// Scr.Enums.StatTone.
export enum TradingStrategyStatTone {
  Unknown = 0,
  Neutral = 1,
  Positive = 2,
  Negative = 3,
}

// Scr.Enums.StatFormat.
export enum TradingStrategyStatFormat {
  Unknown = 0,
  Percent = 1,
  SignedPercent = 2,
  PercentPerYear = 3,
  RatioOfYears = 4,
  Count = 5,
}

export interface TradingStrategyStatDto {
  id: string;
  statKey?: string | null;
  label: string;
  value: string;
  displayOrder: number;
  showOnFrontOffice: boolean;
  showOnBrochureSite: boolean;
  isHighlight: boolean;
  tone: TradingStrategyStatTone;
  // The figure behind `value`, published alongside it. Null for a free-form stat that is text.
  numericValue?: number | null;
  format: TradingStrategyStatFormat;
}

export interface TradingStrategyChartDto {
  id: string;
  name: string;
  blobName: string;
  displayOrder: number;
  chartType: TradingStrategyChartType;
}

export interface TradingStrategyAnnualisedReturnDto {
  series: string;
  period: string;
  label: string;
  value: string;
  numericValue?: number | null;
  displayOrder: number;
}

/** One trade the backtest took. */
export interface TradingStrategyTradeDto {
  tradeNumber: number;
  direction: TradingStrategyTradeDirection;
  entryDate: string;
  entryPrice?: number | null;
  exitDate: string;
  exitPrice?: number | null;
  returnPct: number;
  returnValue: string;
}

/** Which sides a strategy takes. Mirrors Scr.Enums.StrategyDirection. */
export const TradingStrategyDirection = {
  Unknown: 0,
  LongOnly: 1,
  ShortOnly: 2,
  LongAndShort: 3,
} as const;

export type TradingStrategyDirection =
  (typeof TradingStrategyDirection)[keyof typeof TradingStrategyDirection];

export interface TradingStrategyDto {
  id: string;
  publicName: string;
  // The TradingView script's own name. Optional because only the subscriptions payload carries it —
  // it answers "which of my scripts is this?", which is a question only a subscriber has.
  publicStrategyName?: string;
  /** The script's TradingView page. Absent where the strategy has not been published there. */
  publicationUrl?: string | null;
  description: string;
  status: TradingStrategyStatus;
  monthlyPriceUsd: number;
  yearlyPriceUsd: number;
  assetClass: number;
  timeframe: number;
  riskProfile: number;
  // Both optional until the back office starts sending them. A strategy that does not state either
  // shows neither mark, which is the same thing a strategy that genuinely has neither shows.
  direction?: number;
  usesAltSymbols?: boolean;
  /** What the signal is computed on. Absent until the back office republishes the strategy. */
  chartedSymbol?: string | null;
  /** The bull and bear vehicles, where the strategy trades a pair. */
  bullSymbol?: string | null;
  bearSymbol?: string | null;
  chartFrom: string;
  chartTo: string;
  /**
   * The start of the backtest, where it runs further back than the charts do. Null where the two
   * cover the same ground. Not rendered: it is TradingView's Start Limit, which means nothing to a
   * customer without TradingView.
   */
  backtestFrom?: string | null;
  stats: TradingStrategyStatDto[];
  charts: TradingStrategyChartDto[];
  annualisedReturns: TradingStrategyAnnualisedReturnDto[];
  trades: TradingStrategyTradeDto[];
}

interface GetTradingStrategiesResponse {
  items: TradingStrategyDto[];
}

export type BillingCycle = "Monthly" | "Yearly";

const TRADING_STRATEGIES_QUERY_KEY = ["trading-strategies"] as const;

export function useTradingStrategies() {
  return useQuery<TradingStrategyDto[]>({
    queryKey: TRADING_STRATEGIES_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.get<GetTradingStrategiesResponse>(
        "/trading-strategies"
      );
      return res.data.items;
    },
  });
}

function tradingStrategyQuery(id: string) {
  return {
    queryKey: [...TRADING_STRATEGIES_QUERY_KEY, id],
    queryFn: async () => {
      const res = await apiClient.get<TradingStrategyDto>(
        `/trading-strategies/${encodeURIComponent(id)}`
      );
      return res.data;
    },
  };
}

export function useTradingStrategy(id: string) {
  return useQuery<TradingStrategyDto>({
    ...tradingStrategyQuery(id),
    enabled: id.length > 0,
  });
}

function chartContentQuery(strategyId: string, chartId: string | undefined) {
  return {
    queryKey: [...TRADING_STRATEGIES_QUERY_KEY, strategyId, "charts", chartId],
    queryFn: async () => {
      const res = await apiClient.get<ChartPayload>(
        `/trading-strategies/${encodeURIComponent(strategyId)}/charts/${encodeURIComponent(chartId!)}/content`
      );
      return res.data;
    },
    enabled: strategyId.length > 0 && !!chartId,
    // The payload only changes when the back office redraws the chart, which republishes it under a
    // new id — so what is cached here can never go stale under the same key.
    staleTime: Infinity,
    // A chart the customer may not read answers 404 and will answer 404 again. Retrying leaves the
    // spinner up for seconds before the fallback copy appears, which reads as a hang.
    retry: false,
  };
}

/**
 * The drawn payload for one chart.
 *
 * Fetched only when a chart is actually shown: a strategy has two, and putting them on the strategy
 * response would make every caller pay for both. The payload carries its own kind, so nothing else
 * needs to say what the chart is.
 */
export function useTradingStrategyChartContent(
  strategyId: string,
  chartId: string | undefined,
) {
  return useQuery<ChartPayload>(chartContentQuery(strategyId, chartId));
}

/**
 * Loads strategies a page links straight to, with the chart each opens on, so opening one draws at
 * once rather than behind a spinner. The list cannot stand in for them: it leaves out the trades and
 * annualised returns.
 */
export function usePreloadTradingStrategies(
  strategies: Pick<TradingStrategyDto, "id" | "charts">[],
) {
  const strategyQueries = strategies.map((strategy) => tradingStrategyQuery(strategy.id));
  const chartQueries = strategies.map((strategy) =>
    chartContentQuery(strategy.id, strategy.charts[0]?.id),
  );
  useQueries({ queries: [...strategyQueries, ...chartQueries] });
}

export function priceForCycle(
  strategy: Pick<TradingStrategyDto, "monthlyPriceUsd" | "yearlyPriceUsd">,
  cycle: BillingCycle
): number {
  return cycle === "Monthly" ? strategy.monthlyPriceUsd : strategy.yearlyPriceUsd;
}

/**
 * Both cycles at zero, matching the Back Office rule. A strategy free one way and priced the other
 * is not "free" — it is a pricing mistake, and saying Free would hide it.
 */
export function isFreeStrategy(
  strategy: Pick<TradingStrategyDto, "monthlyPriceUsd" | "yearlyPriceUsd">
): boolean {
  return strategy.monthlyPriceUsd === 0 && strategy.yearlyPriceUsd === 0;
}

/**
 * The cycle a URL is asking for. Monthly is the default and is left out of the URL entirely, so an
 * unshared, unfiltered catalogue link stays clean and anything unrecognised falls back to it.
 */
export function parseBillingCycle(raw: string | null): BillingCycle {
  return raw === "Yearly" ? "Yearly" : "Monthly";
}

/**
 * What a yearly price works out at per month — what the annual figure costs, in the units someone
 * has been comparing all the other prices in. Zero when there is no yearly price to divide.
 */
export function monthlyEquivalentUsd(
  strategy: Pick<TradingStrategyDto, "yearlyPriceUsd">
): number {
  return strategy.yearlyPriceUsd > 0 ? strategy.yearlyPriceUsd / 12 : 0;
}

export function annualSavingsPct(
  strategy: Pick<TradingStrategyDto, "monthlyPriceUsd" | "yearlyPriceUsd">
): number {
  const monthlyAnnualised = strategy.monthlyPriceUsd * 12;
  if (monthlyAnnualised <= 0) return 0;

  const saving = monthlyAnnualised - strategy.yearlyPriceUsd;
  return saving > 0 ? Math.round((saving / monthlyAnnualised) * 100) : 0;
}

export function formatChartPeriod(
  strategy: Pick<TradingStrategyDto, "chartFrom" | "chartTo">
): string {
  return `${formatMonthYear(strategy.chartFrom)} - ${formatMonthYear(strategy.chartTo)}`;
}

export function chartDurationMonths(
  strategy: Pick<TradingStrategyDto, "chartFrom" | "chartTo">
): number {
  const start = new Date(strategy.chartFrom);
  const end = new Date(strategy.chartTo);
  return (
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth())
  );
}

export function findStatValue(
  strategy: Pick<TradingStrategyDto, "stats">,
  label: string
): string | undefined {
  const target = label.toLocaleLowerCase();
  return strategy.stats.find((s) => s.label.toLocaleLowerCase() === target)?.value;
}

/**
 * The published figure for a stat, by label.
 *
 * Replaces parsing the display string, which stripped every non-digit: that happened to work for
 * "-16.6%" and turned "11 of 11 yrs" into 1111. The back office publishes the number alongside the
 * text, so nothing here has to reconstruct it.
 *
 * Returns null where the strategy has no such stat, or where the stat is text with no figure —
 * callers decide what that means rather than being handed a 0 that sorts like a real value.
 */
export function statNumber(
  strategy: Pick<TradingStrategyDto, "stats">,
  label: string
): number | null {
  const target = label.toLocaleLowerCase();
  const stat = strategy.stats.find((s) => s.label.toLocaleLowerCase() === target);

  return stat?.numericValue ?? null;
}

/**
 * The published figure for a stat, by its stable key.
 *
 * `statKey` is the contract; `label` is prose and moves whenever the copy does, so anything that
 * computes rather than displays asks for the key. There is deliberately no fallback to the label: a
 * published strategy always carries keys on its core stats, and quietly matching on display text
 * when one is missing would hide the fault rather than show it.
 *
 * Sorting still goes through {@link statNumber}, which was written against labels. Moving both at
 * once would let a change to filtering break the sort order.
 */
export function statNumberByKey(
  strategy: Pick<TradingStrategyDto, "stats">,
  statKey: string
): number | null {
  return strategy.stats.find((s) => s.statKey === statKey)?.numericValue ?? null;
}

/** Sorts descending, with strategies that do not carry the stat last either way. */
export function byStatDescending(label: string) {
  return (a: Pick<TradingStrategyDto, "stats">, b: Pick<TradingStrategyDto, "stats">) =>
    (statNumber(b, label) ?? Number.NEGATIVE_INFINITY) -
    (statNumber(a, label) ?? Number.NEGATIVE_INFINITY);
}

/**
 * Sorts shallowest-first by distance from zero, for figures published as negatives where nearer to
 * zero is better — a drawdown. Plain ascending puts -34.5% ahead of -0.4%, which is the worst
 * strategy first. Magnitude rather than sign so the order survives the figure being published
 * unsigned.
 */
export function byStatMagnitudeAscending(label: string) {
  return (a: Pick<TradingStrategyDto, "stats">, b: Pick<TradingStrategyDto, "stats">) =>
    Math.abs(statNumber(a, label) ?? Number.POSITIVE_INFINITY) -
    Math.abs(statNumber(b, label) ?? Number.POSITIVE_INFINITY);
}

export function primaryStatValue(
  strategy: TradingStrategyDto,
  label: string,
  fallback = "0"
): string {
  return findStatValue(strategy, label) ?? fallback;
}

